import { useId, useMemo } from 'react';
import { channelByKey, formatValue } from '@ccs/core';
import type { LiveSample } from '@ccs/live-contract';
import { formatTime } from '../../../shared/format';

const WINDOW_MS = 5 * 60_000;
const W = 1000;
const H = 150;
const PAD = 10;

type Props = {
  samples: LiveSample[];
  channelKey: string;
  onClose: () => void;
};

/**
 * 实时版 5 分钟曲线：数据来自 useLive 的本地环形缓冲（最多 300 点），
 * 不再请求 /series 接口，也不受回放游标影响。
 */
export default function LiveCurvePanel({ samples, channelKey, onClose }: Props) {
  const channel = channelByKey.get(channelKey);
  const clipId = useId();

  const numeric = useMemo(
    () =>
      samples
        .map((sample) => ({
          t: new Date(sample.ts).getTime(),
          v: Number(sample.values[channelKey]),
        }))
        .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.v)),
    [samples, channelKey],
  );

  const windowEnd = numeric.length ? numeric[numeric.length - 1].t : null;
  const windowStart = windowEnd === null ? null : windowEnd - WINDOW_MS;

  const visible = useMemo(() => {
    if (windowStart === null || windowEnd === null) return [];
    return numeric.filter((point) => point.t >= windowStart && point.t <= windowEnd);
  }, [numeric, windowStart, windowEnd]);

  const lead = useMemo(() => {
    if (windowStart === null) return null;
    for (let i = numeric.length - 1; i >= 0; i -= 1) {
      if (numeric[i].t < windowStart) return numeric[i];
    }
    return null;
  }, [numeric, windowStart]);

  const values = visible.map((point) => point.v);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const current = values.length ? values[values.length - 1] : null;
  const span = max - min;
  const padding = span > 0 ? span * 0.12 : Math.max(Math.abs(max) * 0.02, 0.5);
  const lo = min - padding;
  const hi = max + padding;
  const range = hi - lo || 1;

  const xAt = (t: number) =>
    windowStart === null ? 0 : PAD + ((t - windowStart) / WINDOW_MS) * (W - PAD * 2);
  const yAt = (v: number) => H - PAD - ((v - lo) / range) * (H - PAD * 2);

  const drawable = lead ? [lead, ...visible] : visible;
  const linePoints = drawable
    .map((point) => `${xAt(point.t).toFixed(1)},${yAt(point.v).toFixed(1)}`)
    .join(' ');
  const areaPoints = drawable.length
    ? `${xAt(drawable[0].t).toFixed(1)},${(H - PAD).toFixed(1)} ${linePoints} ${xAt(
        drawable[drawable.length - 1].t,
      ).toFixed(1)},${(H - PAD).toFixed(1)}`
    : '';

  return (
    <section className="panel curve curve-float live-curve">
      <div className="curve-head">
        <div>
          <h2>{channel?.name ?? channelKey}</h2>
          <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
            {channelKey} · {channel?.unit ?? '—'} · 最近 5 分钟（实时）
          </div>
        </div>
        <div className="spacer" />
        <span className="badge accent">
          当前 {current === null ? '—' : formatValue(current, channel)} {channel?.unit ?? ''}
        </span>
        <span className="badge">最小 {values.length ? formatValue(min, channel) : '—'}</span>
        <span className="badge">最大 {values.length ? formatValue(max, channel) : '—'}</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          关闭
        </button>
      </div>

      {visible.length >= 2 ? (
        <>
          <svg className="curve-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
            <defs>
              <clipPath id={clipId}>
                <rect x={PAD} y={0} width={W - PAD * 2} height={H} />
              </clipPath>
              <linearGradient id={`${clipId}-fill`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0f766e" stopOpacity="0.14" />
                <stop offset="100%" stopColor="#0f766e" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map((ratio) => (
              <line
                key={ratio}
                x1={PAD}
                x2={W - PAD}
                y1={PAD + ratio * (H - PAD * 2)}
                y2={PAD + ratio * (H - PAD * 2)}
                stroke="#e2e8f0"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <g clipPath={`url(#${clipId})`}>
              <polygon points={areaPoints} fill={`url(#${clipId}-fill)`} />
              <polyline
                points={linePoints}
                fill="none"
                stroke="#0f766e"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
            <line
              x1={W - PAD}
              x2={W - PAD}
              y1={PAD}
              y2={H - PAD}
              stroke="#0f766e"
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.45"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <div className="curve-axis">
            <span>{formatTime(new Date(windowStart!).toISOString())}</span>
            <span className="curve-live">实时</span>
            <span>{formatTime(new Date(windowEnd!).toISOString())}</span>
          </div>
        </>
      ) : (
        <div className="empty" style={{ padding: 24 }}>
          等待这个测点的实时读数…
        </div>
      )}
    </section>
  );
}